
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { t, i18n } = useTranslation();
  const { isDark, toggleTheme, isTransitioning } = useTheme();

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    setScrolled(currentScrollY > 50);

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setHidden(true);
    } else {
      setHidden(false);
    }

    setLastScrollY(currentScrollY);

    const sections = [
      'core-capabilities',
      'architecture',
      'developers',
      'rwa',
      'consensus',
      'rmind',
    ];
    for (const section of sections) {
      const element = document.getElementById(section);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom >= 150) {
          setActiveSection(section);
          break;
        }
      }
    }
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // 关闭移动端菜单时禁止滚动
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'zh' : 'en';
    i18n.changeLanguage(newLang).catch((err) => {
      console.error('Failed to change language:', err);
    });
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { href: '#core-capabilities', label: t('nav.capabilities'), id: 'core-capabilities' },
    { href: '#architecture', label: t('nav.architecture'), id: 'architecture' },
    { href: '#developers', label: t('nav.developers'), id: 'developers' },
    { href: '#rwa', label: t('nav.rwa'), id: 'rwa' },
    { href: '#rmind', label: t('nav.rmind'), id: 'rmind' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? isDark
              ? 'bg-[#0d1117]/95 backdrop-blur-xl shadow-lg shadow-black/20 border-b border-white/5'
              : 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5 border-b border-black/5'
            : 'bg-transparent'
        }`}
      >
        {/* 顶部进度条 */}
        <div
          className="absolute top-0 left-0 h-[2px] bg-gradient-to-r from-[#E84A1F] to-[#F06030] transition-all duration-300"
          style={{
            width: `${Math.min(
              (lastScrollY /
                (document.documentElement.scrollHeight - window.innerHeight)) *
                100,
              100,
            )}%`,
          }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
            {/* Logo 区域 */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-fit flex-shrink-0">
              <div className="group relative w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-[#E84A1F]/20 to-[#F06030]/20 rounded-lg md:rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                <div className="relative w-full h-full bg-white rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#E84A1F]/20 overflow-hidden group-hover:scale-110 transition-all duration-700 p-1 sm:p-1.5">
                  <img 
                    src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/d509b5c7881d9da6657335b51d03b561.png" 
                    alt="RoonChain Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="flex flex-col flex-shrink-0">
                <span className={`text-base sm:text-lg md:text-xl font-bold whitespace-nowrap transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0a0a0a]'}`}>
                  RoonChain
                </span>
                <span className="text-[8px] sm:text-[9px] md:text-[10px] text-[#E84A1F] tracking-wider whitespace-nowrap hidden sm:block">
                  Next-Gen RWA Infrastructure
                </span>
              </div>
            </div>

            {/* 中间导航链接 - 大屏幕显示 */}
            <div className="hidden lg:flex items-center gap-1 flex-1 justify-center mx-4 xl:mx-8">
              {navLinks.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`group relative px-3 xl:px-4 py-2 rounded-lg transition-all duration-300 whitespace-nowrap ${
                    activeSection === item.id
                      ? 'text-[#E84A1F]'
                      : isDark
                      ? 'text-gray-300 hover:text-white hover:bg-white/5'
                      : 'text-gray-600 hover:text-[#0a0a0a] hover:bg-black/5'
                  }`}
                >
                  <span className="relative z-10 text-xs xl:text-sm font-medium">{item.label}</span>
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-[#E84A1F] to-[#F06030] transition-all duration-300 ${
                    activeSection === item.id ? 'w-3/4' : 'w-0 group-hover:w-3/4'
                  }`}></div>
                </a>
              ))}
            </div>

            {/* 右侧按钮组 - 大屏幕 */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3 min-w-fit flex-shrink-0">
              {/* Theme Toggle */}
              <button
                onClick={(e) => toggleTheme(e)}
                disabled={isTransitioning}
                className={`theme-toggle-btn group relative w-9 h-9 xl:w-10 xl:h-10 rounded-lg xl:rounded-xl flex items-center justify-center transition-all duration-500 cursor-pointer border ${
                  isTransitioning ? 'pointer-events-none' : ''
                } ${
                  scrolled
                    ? isDark
                      ? 'text-gray-400 hover:text-[#E84A1F] border-gray-700/50 hover:border-[#E84A1F]/50 hover:bg-[#E84A1F]/10'
                      : 'text-[#0d1117]/60 hover:text-[#E84A1F] border-gray-200 hover:border-[#E84A1F]/50 hover:bg-[#E84A1F]/10'
                    : 'text-gray-400 hover:text-[#E84A1F] border-gray-700/50 hover:border-[#E84A1F]/50 hover:bg-[#E84A1F]/10'
                }`}
              >
                <span className={`absolute inset-0 rounded-lg xl:rounded-xl transition-all duration-500 ${
                  isDark 
                    ? 'bg-gradient-to-br from-yellow-500/0 to-orange-500/0 group-hover:from-yellow-500/20 group-hover:to-orange-500/10' 
                    : 'bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/20 group-hover:to-purple-500/10'
                }`}></span>
                
                <span className="relative z-10 w-5 h-5 flex items-center justify-center">
                  <i className={`ri-sun-line absolute text-lg transition-all duration-500 ${
                    isDark ? 'opacity-100 rotate-0 scale-100 theme-icon-sun' : 'opacity-0 -rotate-90 scale-0'
                  }`}></i>
                  <i className={`ri-moon-line absolute text-lg transition-all duration-500 ${
                    isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100 theme-icon-moon'
                  }`}></i>
                </span>
              </button>

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`group relative px-3 xl:px-5 py-2 xl:py-2.5 transition-all duration-500 whitespace-nowrap cursor-pointer overflow-hidden rounded-lg border ${
                  scrolled
                    ? isDark
                      ? 'text-gray-400 hover:text-white border-gray-700/50 hover:border-[#E84A1F]/50'
                      : 'text-[#0d1117]/60 hover:text-[#0d1117] border-gray-200 hover:border-[#E84A1F]/50'
                    : 'text-gray-400 hover:text-white border-gray-700/50 hover:border-[#E84A1F]/50'
                }`}
              >
                <span className="relative z-10 flex items-center gap-1.5 xl:gap-2 text-sm">
                  <i className="ri-global-line group-hover:rotate-180 transition-transform duration-500"></i>
                  {i18n.language === 'en' ? '中文' : 'EN'}
                </span>
                <span className="absolute inset-0 bg-[#E84A1F]/10 scale-0 group-hover:scale-100 transition-transform duration-300"></span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center gap-1 sm:gap-2">
              {/* Mobile Theme Toggle */}
              <button
                onClick={(e) => toggleTheme(e)}
                disabled={isTransitioning}
                className={`theme-toggle-btn relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center cursor-pointer rounded-lg transition-all duration-500 ${
                  isTransitioning ? 'pointer-events-none' : ''
                } ${
                  scrolled ? (isDark ? 'text-white' : 'text-[#0d1117]') : 'text-white'
                }`}
              >
                <span className="relative w-5 h-5 flex items-center justify-center">
                  <i className={`ri-sun-line absolute text-lg sm:text-xl transition-all duration-500 ${
                    isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
                  }`}></i>
                  <i className={`ri-moon-line absolute text-lg sm:text-xl transition-all duration-500 ${
                    isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
                  }`}></i>
                </span>
              </button>

              {/* Language Toggle - Mobile */}
              <button
                onClick={toggleLanguage}
                className={`relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center cursor-pointer rounded-lg transition-all duration-500 ${
                  scrolled ? (isDark ? 'text-white' : 'text-[#0d1117]') : 'text-white'
                }`}
              >
                <i className="ri-global-line text-lg sm:text-xl"></i>
              </button>

              {/* Hamburger Menu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center cursor-pointer group transition-colors duration-500 ${
                  scrolled ? (isDark ? 'text-white' : 'text-[#0d1117]') : 'text-white'
                }`}
              >
                <div
                  className={`absolute w-5 h-0.5 transition-all duration-300 ${
                    mobileMenuOpen ? 'rotate-45' : '-translate-y-1.5'
                  } ${scrolled ? (isDark ? 'bg-white' : 'bg-[#0d1117]') : 'bg-white'}`}
                ></div>
                <div
                  className={`absolute w-5 h-0.5 transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  } ${scrolled ? (isDark ? 'bg-white' : 'bg-[#0d1117]') : 'bg-white'}`}
                ></div>
                <div
                  className={`absolute w-5 h-0.5 transition-all duration-300 ${
                    mobileMenuOpen ? '-rotate-45' : 'translate-y-1.5'
                  } ${scrolled ? (isDark ? 'bg-white' : 'bg-[#0d1117]') : 'bg-white'}`}
                ></div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} backdrop-blur-sm`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>
        
        {/* Menu Content */}
        <div 
          className={`absolute top-14 sm:top-16 left-0 right-0 max-h-[calc(100vh-3.5rem)] sm:max-h-[calc(100vh-4rem)] overflow-y-auto transition-all duration-500 ${
            isDark ? 'bg-[#0d1117]' : 'bg-white'
          } ${mobileMenuOpen ? 'translate-y-0' : '-translate-y-4'}`}
        >
          <div className="px-4 py-4 sm:py-6 space-y-1 sm:space-y-2">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`block px-4 py-3 sm:py-4 text-base sm:text-lg rounded-xl transition-all duration-500 cursor-pointer ${
                  mobileMenuOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'
                } ${activeSection === link.id
                  ? 'text-[#E84A1F] bg-[#E84A1F]/10'
                  : isDark
                  ? 'text-gray-300 hover:text-white hover:bg-white/5'
                  : 'text-gray-600 hover:text-[#0d1117] hover:bg-gray-100'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {link.label}
              </a>
            ))}
          </div>
          
          {/* Bottom Safe Area */}
          <div className="h-6 sm:h-8"></div>
        </div>
      </div>
    </>
  );
}
